<template>
  <div>
    <div class="clientSelector" style="z-index: 699">
      <CustomerSearch
        v-if="!deviceStore.isInternalStore"
        @openAddNewCustomer="handleCustomerDialogDisplay()"
      />
      <wa-dropdown
        v-if="customer.id"
        ref="customerMenuDropdown"
        class="customer-actions-menu"
        placement="bottom-end"
        distance="2"
        style="margin-left: var(--wa-space-xs)"
      >
        <wa-button
          variant="neutral"
          appearance="outlined"
          slot="trigger"
          data-testid="menu-customer-dropdown"
          with-caret
        >
          <div
            class="dropdown-button-content"
            :class="[
              {
                'dropdown-button-content--warning':
                  customer.metadata?.restrictions === CUSTOMER_RESTRICTION.PREPAYMENT
              },
              {
                'dropdown-button-content--danger':
                  isFraud ||
                  [CUSTOMER_RESTRICTION.BANNED, CUSTOMER_RESTRICTION.BLOCKED].includes(
                    customer.metadata?.restrictions
                  )
              }
            ]"
          >
            <template
              v-if="
                isFraud ||
                [CUSTOMER_RESTRICTION.BANNED, CUSTOMER_RESTRICTION.BLOCKED].includes(
                  customer.metadata?.restrictions
                )
              "
            >
              <PhWarning :size="18" />
            </template>

            <template v-if="customer.metadata?.restrictions === CUSTOMER_RESTRICTION.PREPAYMENT">
              <PhHandCoins :size="18" />
            </template>

            <span class="dropdown-button-content__name">
              <template v-if="customer.metadata?.first_name">
                {{ customer.metadata?.first_name }} {{ customer.metadata?.last_name }}
              </template>
              <template v-else>{{ customer.email }}</template>
            </span>
          </div>
        </wa-button>
        <wa-dropdown-item @click="handleOpenDrawer('profile')">
          <div class="customer-actions-menu__item" data-testid="menu-customer-profile-option">
            <PhUser class="customer-actions-menu__item-icon" weight="regular" :size="20" />
            <p class="customer-actions-menu__item-label">
              {{ $t('customer.menu.customer_profile') }}
            </p>
          </div>
        </wa-dropdown-item>
        <wa-dropdown-item @click="handleOpenDrawer('orders')">
          <div class="customer-actions-menu__item">
            <PhPackage class="customer-actions-menu__item-icon" weight="regular" :size="20" />
            <p class="customer-actions-menu__item-label">{{ $t('customer.menu.orders') }}</p>
          </div>
          <wa-badge v-if="customer.total_orders_count" class="customer-actions-menu__item-badge">{{
            customer.total_orders_count
          }}</wa-badge>
        </wa-dropdown-item>
        <wa-dropdown-item @click="handleOpenDrawer('prescriptions')">
          <div class="customer-actions-menu__item">
            <PhEyeglasses class="customer-actions-menu__item-icon" weight="regular" :size="20" />
            <p class="customer-actions-menu__item-label">
              {{ $t('customer.menu.prescriptions') }}
            </p>
          </div>
        </wa-dropdown-item>
        <wa-dropdown-item @click="handleOpenDrawer('vouchers')">
          <div class="customer-actions-menu__item">
            <PhCards class="customer-actions-menu__item-icon" weight="regular" :size="20" />
            <p class="customer-actions-menu__item-label">
              {{ $t('sidebar.vouchersAndCoupons') }}
            </p>
          </div>
        </wa-dropdown-item>
        <wa-dropdown-item @click="handleOpenSummary">
          <div class="customer-actions-menu__item">
            <PhSparkle class="customer-actions-menu__item-icon" weight="regular" :size="20" />
            <p class="customer-actions-menu__item-label">
              {{ $t('customer.summary.title') }}
            </p>
          </div>
        </wa-dropdown-item>
        <template
          v-if="
            (customer.metadata?.restrictions && customer.metadata?.restrictions !== 'null') ||
            customer.metadata?.notes
          "
        >
          <wa-divider></wa-divider>
          <wa-dropdown-item class="dense">
            <wa-icon library="bootstrap" slot="icon" name="exclamation-triangle"></wa-icon>
            <div class="dense" style="font-size: var(--wa-font-size-s)">
              <b
                v-if="customer.metadata?.restrictions === CUSTOMER_RESTRICTION.BLOCKED"
                style="color: var(--wa-color-danger-40)"
                >{{ $t('cart.alert.customerResctrictionBlocked') }}</b
              >
              <b
                v-if="customer.metadata?.restrictions === CUSTOMER_RESTRICTION.BANNED"
                style="color: var(--wa-color-danger-40)"
                >{{ $t('cart.alert.customerResctrictionBanned') }}</b
              >
              <b
                v-if="customer.metadata?.restrictions === CUSTOMER_RESTRICTION.PREPAYMENT"
                style="color: var(--wa-color-warning-50)"
                >{{ $t('cart.alert.customerRestrictionPrePayment') }}</b
              >
              <div v-if="customer.metadata?.notes">{{ customer.metadata.notes }}</div>
            </div>
          </wa-dropdown-item>
        </template>
        <wa-divider class="customer-actions-menu__divider" />
        <wa-dropdown-item @click="handleCustomerSignOut">
          <div class="customer-actions-menu__item">
            <PhSignOut class="customer-actions-menu__item-icon" weight="regular" :size="20" />
            <p class="customer-actions-menu__item-sign-out-label">
              {{ $t('globals.sign_out') }}
            </p>
          </div>
        </wa-dropdown-item>
      </wa-dropdown>
    </div>
    <section class="drawers">
      <CustomerProfileComp
        v-if="currentCustomerDrawer === 'profile'"
        @openEditCustomerDialog="handleCustomerDialogDisplay()"
      />
      <CustomerVouchersComp v-if="currentCustomerDrawer === 'vouchers'" />
      <CustomerPrescriptionsComp v-if="currentCustomerDrawer === 'prescriptions'" />
      <CustomerOrdersComp v-if="currentCustomerDrawer === 'orders'" />
      <CustomerOrderDetailsComp v-if="uiStore.showCustomerOrderDetails" />
      <FulfillmentDetails v-if="fulfillmentStore.fulfillmentId" />
      <IncomingGoodsParcelDrawer v-if="uiStore.showIncomingGoodsParcelDrawer" />
    </section>

    <section class="dialogs">
      <CustomerAddressAddOrEditDialog
        v-if="uiStore.showCustomerAddressCreationEditDialog"
        :is-delete-address-button-visible="currentCustomerDrawer === 'profile'"
      />
      <CustomerAddOrEditDialogDialogComp v-if="uiStore.showCustomerCreationEditDialog" />
      <CustomerAnonymiseDialogComp v-if="uiStore.showAnonymiseCustomerDialog" />
      <CustomerAddPrescriptionComp
        v-if="uiStore.showAddPrescriptionDialog"
        @refetchPrescriptions="$emit('refetchPrescriptions')"
      />
      <CartDialogComp v-if="uiStore.showCart" />

      <CartChooseAddressDrawer v-if="uiStore.showCartChooseAddressDrawer" />
      <StockTransferDetailDialog v-if="uiStore.showStockTransferDetails" />
      <CustomerSummary
        v-model:open="summaryOpen"
        :customer-id="customer.id"
        :customer-email="customer.email"
      />
    </section>
  </div>
</template>

<script setup>
import { useCartStore } from '@/stores/cart.js'
import { useCustomerStore } from '@/stores/customer.js'
import { useDeviceStore } from '@/stores/device'
import { useFulfillmentStore } from '@/stores/fulfillment.js'
import { useWishlists } from '@/composables/useWishlists.js'
import { useUiStore } from '@/stores/ui.js'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, ref } from 'vue'
import CustomerSearch from '../CustomerSearch/CustomerSearch.vue'

import CartChooseAddressDrawer from '@/components/Cart/CartChooseAddressDrawer/CartChooseAddressDrawer.vue'
import { CUSTOMER_RESTRICTION } from '@/constants/customer.js'
import {
  PhCards,
  PhEyeglasses,
  PhHandCoins,
  PhUser,
  PhPackage,
  PhSignOut,
  PhSparkle,
  PhWarning
} from '@phosphor-icons/vue'
import '@awesome.me/webawesome/dist/components/badge/badge.js'
import '@awesome.me/webawesome/dist/components/button/button.js'
import '@awesome.me/webawesome/dist/components/dialog/dialog.js'
import '@awesome.me/webawesome/dist/components/divider/divider.js'
import '@awesome.me/webawesome/dist/components/dropdown/dropdown.js'
import '@awesome.me/webawesome/dist/components/icon/icon.js'
import '@awesome.me/webawesome/dist/components/dropdown-item/dropdown-item.js'
import { useRouter } from 'vue-router'

const CustomerAddOrEditDialogDialogComp = defineAsyncComponent(
  () => import('@/components/Customer/CustomerAddOrEditDialog/CustomerAddOrEditDialog.vue')
)
const CustomerAddPrescriptionComp = defineAsyncComponent(
  () => import('@/components/Customer/CustomerPrescriptions/CustomerAddPrescriptionDialog.vue')
)
const CustomerProfileComp = defineAsyncComponent(
  () => import('@/components/Customer/CustomerProfile/CustomerProfile.vue')
)
const CustomerVouchersComp = defineAsyncComponent(
  () => import('@/components/Customer/CustomerVouchers/CustomerVouchers.vue')
)
const CustomerPrescriptionsComp = defineAsyncComponent(
  () => import('@/components/Customer/CustomerPrescriptions/CustomerPrescriptions.vue')
)
const CustomerOrdersComp = defineAsyncComponent(
  () => import('@/components/Customer/CustomerOrders/CustomerOrders.vue')
)
const CustomerOrderDetailsComp = defineAsyncComponent(
  () => import('@/components/Customer/CustomerOrderDetails/CustomerOrderDetails.vue')
)
const CustomerAnonymiseDialogComp = defineAsyncComponent(
  () => import('@/components/Customer/CustomerAnonymiseDialog/CustomerAnonymiseDialog.vue')
)
const CartDialogComp = defineAsyncComponent(
  () => import('@/components/Cart/CartDialog/CartDialog.vue')
)

const CustomerAddressAddOrEditDialog = defineAsyncComponent(
  () =>
    import(
      '@/components/Customer/CustomerAddressAddOrEditDialog/CustomerAddressAddOrEditDialog.vue'
    )
)

const StockTransferDetailDialog = defineAsyncComponent(
  () => import('@/components/StockTransfer/StockTransferDetailDialog/StockTransferDetailDialog.vue')
)

const IncomingGoodsParcelDrawer = defineAsyncComponent(
  () => import('@/components/IncomingGoods/IncomingGoodsParcelDrawer/IncomingGoodsParcelDrawer.vue')
)

const FulfillmentDetails = defineAsyncComponent(
  () => import('@/components/Fulfillment/FulfillmentDetails/FulfillmentDetails.vue')
)

const CustomerSummary = defineAsyncComponent(
  () => import('@/components/Customer/CustomerSummary/CustomerSummary.vue')
)

const router = useRouter()
const customerStore = useCustomerStore()
const deviceStore = useDeviceStore()
const cartStore = useCartStore()
const uiStore = useUiStore()
const fulfillmentStore = useFulfillmentStore()
const { wishlistsReset } = useWishlists()
const customer = computed(() => customerStore.getCustomer)
const isFraud = computed(() => customer.value?.tags?.some((tag) => tag.name === 'fraud'))
const { currentCustomerDrawer } = storeToRefs(uiStore)
const customerMenuDropdown = ref()

const summaryOpen = ref(false)

function handleOpenSummary() {
  if (customerMenuDropdown.value?.open) {
    customerMenuDropdown.value.open = false
  }
  summaryOpen.value = true
}

function handleCustomerDialogDisplay() {
  if (customerMenuDropdown.value?.open) {
    customerMenuDropdown.value.open = !customerMenuDropdown.value?.open
  }

  customerStore.isCustomerProfileEdit = false
  uiStore.showCustomerCreationEditDialog = true
}

function handleCustomerSignOut() {
  wishlistsReset()
  cartStore.$reset()
  customerStore.reset()
  uiStore.showCart = false
  router.push({ name: 'home' })
}

function handleOpenDrawer(name) {
  if (customerMenuDropdown.value?.open) {
    customerMenuDropdown.value.open = !customerMenuDropdown.value?.open
  }
  uiStore.$patch({ currentCustomerDrawer: name })
}
</script>
<style lang="postcss" scoped src="./CustomerMenu.css" />
